import { LockerSlotStatus, LockerStatusData } from './types';

/**
 * 라커 그리드의 표시·전송 규칙을 한곳에 모은다.
 *
 * 화면 컴포넌트에 흩어 두면 "무엇을 보여줄 것인가"와 "무엇을 서버로 보낼 것인가"가 섞여
 * 값의 의미가 조용히 바뀐다. 특히 아래 두 가지는 틀리면 되돌릴 수 없는 데이터 오염이라
 * 순수 함수로 떼어 내고 테스트로 고정한다.
 *
 *   ① 배정에서 "칸 고정값 사용"을 골랐을 때 `lockNumber: null` **을 명시해 개별값을 비우는 것**
 *      (키를 빼면 서버가 미전송을 "변경 없음"으로 읽어 옛 개별값이 그대로 남는다)
 *   ② 일괄 입력이 다루는 값은 **오직 칸의 `defaultLockNumber`** 라는 것
 *      (화면에 보이는 effective 값을 초기값으로 쓰면 개별값이 고정값으로 복제된다)
 */

export type LockerSemanticTone = 'success' | 'warning' | 'danger';

export const LOCK_NUMBER_MASK = '••••';

/** 정지(PAUSED)도 점유다. 배정 대상이 아니고 액션 메뉴가 열려야 한다. */
export const isOccupiedStatus = (status: LockerSlotStatus): boolean =>
    status === 'ASSIGNED' || status === 'PAUSED' || status === 'EXPIRED';

/**
 * 그 칸에서 지금 실제로 통하는 비밀번호.
 *
 * 점유 칸은 서버가 `subscription.lockNumber` 에 이미 `개별값 || 칸 고정값` 을 실어 준다.
 * 빈 칸은 이용권이 없으므로 칸의 고정값이 곧 유효값이다.
 */
export const getEffectiveLockNumber = (locker: LockerStatusData): string | null => {
    if (locker.subscription) return locker.subscription.lockNumber || null;
    return locker.defaultLockNumber || null;
};

/**
 * 이 회원이 칸의 고정값을 **따라가지 않는지**.
 *
 * 판정 기준은 **개별값의 존재 여부 하나**다. 값이 지금 고정값과 같더라도 DB 에 개별값이
 * 남아 있는 한 이후의 고정값 변경을 상속하지 않는다 — 지금 같은 것은 우연일 뿐이다.
 * 값을 비교해서 "다를 때만" 표시하면, 관리자는 고정값을 바꾼 뒤에야 그 회원만 안 따라온
 * 것을 알게 된다.
 */
export const isIndividualOverride = (locker: LockerStatusData): boolean =>
    !!(locker.subscription?.ownLockNumber || null);

/** 남은 일수 → 배지. 기존 규칙을 그대로 유지한다. */
export const getDdayBadge = (dday: number): { text: string; tone: LockerSemanticTone } => {
    if (dday < 0) return { text: '만료', tone: 'danger' };
    if (dday <= 7) return { text: `D-${dday}`, tone: 'danger' };
    if (dday <= 30) return { text: `D-${dday}`, tone: 'warning' };
    return { text: `D-${dday}`, tone: 'success' };
};

/**
 * 카드에 붙는 배지 **하나**. 우선순위가 곧 규칙이다.
 *
 *   PAUSED → '정지'  (D-day 를 **대체**한다. 정지 중에는 기간이 멈춰 D-day 가 거짓이 된다.)
 *   EXPIRED → '만료'
 *   ASSIGNED → D-N
 *   AVAILABLE → 없음
 *
 * 하나만 그리므로 같은 카드에 앰버 배지가 둘 겹치지 않는다.
 */
export const getStatusBadge = (
    locker: LockerStatusData,
    calculateDday: (endDate: string) => number,
): { text: string; tone: LockerSemanticTone } | null => {
    if (locker.status === 'PAUSED') return { text: '정지', tone: 'warning' };
    if (locker.status === 'EXPIRED') return { text: '만료', tone: 'danger' };
    if (locker.status === 'ASSIGNED' && locker.subscription) {
        return getDdayBadge(calculateDday(locker.subscription.endDate));
    }
    return null;
};

/** 값이 있으면 가리고, 없으면 '—'. 공개 여부는 호출부가 정한다. */
export const formatLockNumber = (value: string | null | undefined, revealed: boolean): string => {
    if (!value) return '—';
    return revealed ? value : LOCK_NUMBER_MASK;
};

export type AssignLockNumberMode = 'inherit' | 'own';

/**
 * 배정 요청에 실을 `lockNumber` 조각.
 *
 * ⚠️ **키를 생략하면 안 된다.** 서버(`facilitySubscription.service.ts:1671`)는
 *
 *     if (body.lockNumber !== undefined) { subscription.lockNumber = resolve(body.lockNumber); }
 *
 * 즉 **미전송을 "변경 없음"으로 읽는다.** 개별값 `1111` 을 들고 있는 이용권을 고정값 `9999`
 * 칸에 「고정값 사용」으로 배정하면, 키를 빼는 순간 `1111` 이 그대로 남는다. 관리자는 9999 를
 * 골랐는데 화면에는 1111 이 뜬다.
 *
 * 그래서 두 경우 모두 **`null` 을 명시**해 개별값을 비운다. 서버의 `resolveLockNumberInput`
 * 이 `null`/`''` 을 "명시적 비움"으로 받는다.
 *
 *   - 'inherit'          → 개별값을 비워 칸의 고정값을 따라가게 한다
 *   - 'own' + 빈 입력    → 화면의 빈 칸이 곧 "개별값 없음"이다 (모달이 후보의 저장값을
 *                          미리 채우므로 빈 칸은 지우겠다는 뜻이다 — 프리필이 이 규약의 전제다)
 *
 * 비밀번호 없이 배정하는 경로(실측 47.3%)는 그대로 산다 — `null` 은 "비밀번호 없음"이라
 * 결과가 같고, 배정 자체를 막는 조건이 아니다.
 */
export const buildAssignLockNumberPayload = ({
    mode,
    ownLockNumber,
}: {
    mode: AssignLockNumberMode;
    ownLockNumber?: string | null;
}): { lockNumber: string | null } => {
    if (mode === 'inherit') return { lockNumber: null };
    const trimmed = (ownLockNumber ?? '').trim();
    return { lockNumber: trimmed === '' ? null : trimmed };
};

/**
 * 일괄 입력 셀의 초기값.
 *
 * ⚠️ **오직 칸의 `defaultLockNumber` 다.** `getEffectiveLockNumber` 를 쓰면 개별값이
 * 초기값으로 들어오고, 그 상태에서 저장하면 **회원의 개별값이 칸의 고정값으로 복제**된다.
 * 그 칸을 나중에 쓸 모든 회원에게 남의 비밀번호가 상속되는 셈이라 의미가 완전히 바뀐다.
 */
export const getBulkInitialValue = (locker: LockerStatusData): string => locker.defaultLockNumber ?? '';

/**
 * 고정 비밀번호 저장 본문.
 *
 * `defaultLockNumber` 는 **항상 키를 포함**한다(빈 값이면 `null` = 해제).
 * 이 화면에서 저장을 누르는 것은 언제나 "이 값으로 두겠다"는 뜻이지 "건드리지 않겠다"가
 * 아니기 때문이다. "변경 없음"이 필요한 호출부가 생기면 그때 키를 빼면 된다.
 */
export const buildSlotUpdateBody = ({
    defaultLockNumber,
    unifyExisting,
}: {
    defaultLockNumber: string;
    unifyExisting?: boolean;
}): { defaultLockNumber: string | null; unifyExisting?: boolean } => {
    const trimmed = defaultLockNumber.trim();
    return {
        defaultLockNumber: trimmed === '' ? null : trimmed,
        ...(unifyExisting ? { unifyExisting: true } : {}),
    };
};

export type LockerStatusFilter = 'all' | 'assigned' | 'paused' | 'expired' | 'available';

export const LOCKER_STATUS_FILTERS: { code: LockerStatusFilter; label: string }[] = [
    { code: 'all', label: '전체' },
    { code: 'assigned', label: '배정' },
    { code: 'paused', label: '정지' },
    { code: 'expired', label: '만료' },
    { code: 'available', label: '비어있음' },
];

/** 정지는 배정에 섞지 않는다. 회수·연장 판단이 달라서 같이 세면 안 된다. */
export const matchesLockerFilter = (locker: LockerStatusData, filter: LockerStatusFilter): boolean => {
    if (filter === 'all') return true;
    if (filter === 'assigned') return locker.status === 'ASSIGNED';
    if (filter === 'paused') return locker.status === 'PAUSED';
    if (filter === 'expired') return locker.status === 'EXPIRED';
    return locker.status === 'AVAILABLE';
};

const onlyDigits = (value: string) => value.replace(/\D/g, '');

/**
 * 번호 / 회원명 / 연락처 검색.
 *
 * 연락처는 하이픈 유무가 제각각이라 숫자만 남겨 비교한다.
 * **비밀번호는 검색 대상이 아니다** — 마스킹해 놓고 검색으로 찾아지면 가린 의미가 없다.
 */
export const matchesLockerSearch = (locker: LockerStatusData, rawQuery: string): boolean => {
    const query = rawQuery.trim();
    if (query === '') return true;

    // 번호는 앞자리 일치까지 허용한다("12" → 12, 120~129).
    // 연락처처럼 0 으로 시작하는 질의는 라커 번호(1 이상)와 겹치지 않는다.
    const numberText = String(locker.number);
    if (numberText === query || numberText.startsWith(query)) return true;

    const sub = locker.subscription;
    if (!sub) return false;

    const lowered = query.toLowerCase();
    if ((sub.memberName || '').toLowerCase().includes(lowered)) return true;

    const digits = onlyDigits(query);
    if (digits !== '' && onlyDigits(sub.phoneNumber || '').includes(digits)) return true;

    return false;
};
